import React, { useId, useRef, useState } from 'react';
import type { FileUploadProps } from './types';

// ─── Icons ────────────────────────────────────────────────────────────────────

function UploadIcon() {
  return (
    <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="16 16 12 12 8 16" />
      <line x1="12" y1="12" x2="12" y2="21" />
      <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function PaperclipIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MIME_LABELS: Record<string, string> = {
  'image/png': 'PNG',
  'image/jpeg': 'JPG',
  'image/jpg': 'JPG',
  'image/webp': 'WebP',
  'image/gif': 'GIF',
  'image/svg+xml': 'SVG',
  'application/pdf': 'PDF',
  'text/csv': 'CSV',
};

/** "image/png,image/jpeg" → "PNG or JPG"; ".csv" → "CSV". */
function describeAccept(accept: string): string {
  const labels = accept
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => MIME_LABELS[t] ?? t.replace(/^\./, '').replace(/^[a-z]+\//, '').toUpperCase());
  const unique = Array.from(new Set(labels));
  if (unique.length <= 1) return unique[0] ?? 'Any file';
  return `${unique.slice(0, -1).join(', ')} or ${unique[unique.length - 1]}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── FileUpload ───────────────────────────────────────────────────────────────

export function FileUpload({
  onUpload,
  accept,
  maxSize = 5 * 1024 * 1024,
  multiple = false,
  variant = 'dropzone',
  preview = null,
  error,
  loading = false,
  progress,
}: FileUploadProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const progressId = `${id}-progress`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);

  const displayError = error ?? sizeError;
  const hasError = Boolean(displayError);
  const isUploading = loading || (progress !== undefined && progress < 100);
  const isUploaded = Boolean(preview) && !isUploading;

  function processFiles(files: FileList | File[]) {
    const arr = Array.from(files);
    const oversized = arr.filter(f => f.size > maxSize);
    if (oversized.length > 0) {
      setSizeError(`File exceeds maximum size of ${formatBytes(maxSize)}`);
      return;
    }
    setSizeError(null);
    onUpload?.(multiple ? arr : arr.slice(0, 1));
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.length) processFiles(e.target.files);
    // Reset so same file can be re-selected
    e.target.value = '';
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragging(true);
  }

  function handleDragLeave() {
    setDragging(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) processFiles(e.dataTransfer.files);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputRef.current?.click();
    }
  }

  // ─── Hidden file input (shared by all variants) ────────────────────────────
  const fileInput = (
    <input
      ref={inputRef}
      id={id}
      type="file"
      accept={accept}
      multiple={multiple}
      capture={variant === 'camera' ? 'environment' : undefined}
      className="sr-only"
      aria-describedby={hasError ? errorId : undefined}
      onChange={handleInputChange}
    />
  );

  // ─── Uploaded preview (shared between dropzone / camera) ──────────────────
  if (isUploaded && variant !== 'button') {
    return (
      <div className="flex flex-col gap-1">
        <div className="relative inline-flex rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-700 w-full max-w-xs">
          <img src={preview!} alt="Uploaded preview" className="w-full h-48 object-cover" />
          <button
            type="button"
            className="absolute top-2 right-2 p-1 rounded-full bg-white/90 dark:bg-neutral-900/90 text-neutral-700 dark:text-neutral-300 hover:bg-white dark:hover:bg-neutral-900 shadow transition-colors"
            onClick={() => onUpload?.([])}
            aria-label="Remove file"
          >
            <XIcon />
          </button>
        </div>
        {fileInput}
      </div>
    );
  }

  // ─── Button variant ────────────────────────────────────────────────────────
  if (variant === 'button') {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <label
            htmlFor={id}
            className={[
              'inline-flex items-center gap-1.5 px-3 h-9 text-sm font-medium rounded border cursor-pointer transition-colors select-none',
              'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300',
              'border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700',
              isUploading ? 'opacity-70 cursor-not-allowed pointer-events-none' : '',
            ].filter(Boolean).join(' ')}
          >
            <PaperclipIcon />
            {isUploading ? 'Uploading…' : 'Attach file'}
          </label>
          {preview && (
            <div className="flex items-center gap-1 text-sm text-neutral-500 dark:text-neutral-400">
              <span className="truncate max-w-[160px]">{preview.split('/').pop()}</span>
              <button
                type="button"
                className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
                onClick={() => onUpload?.([])}
                aria-label="Remove file"
              >
                <XIcon />
              </button>
            </div>
          )}
        </div>
        {fileInput}
        {isUploading && progress !== undefined && (
          <ProgressBar progress={progress} progressId={progressId} />
        )}
        {hasError && <ErrorText id={errorId} message={displayError!} />}
      </div>
    );
  }

  // ─── Dropzone / Camera variants ───────────────────────────────────────────
  const dropzoneClasses = [
    'relative flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed',
    'transition-colors duration-150 cursor-pointer select-none outline-none',
    'focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
    variant === 'camera' ? 'h-40' : 'h-48',
    isUploading
      ? 'border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800/50 cursor-not-allowed pointer-events-none'
      : hasError
        ? 'border-danger-500 bg-danger-50/30 dark:bg-danger-900/10 hover:border-danger-600'
        : dragging
          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
          : 'border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 hover:border-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/10',
  ].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={id}
        role="button"
        tabIndex={isUploading ? -1 : 0}
        className={dropzoneClasses}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
        aria-describedby={[hasError ? errorId : '', isUploading ? progressId : ''].filter(Boolean).join(' ') || undefined}
      >
        {isUploading ? (
          <div className="flex flex-col items-center gap-2 w-full px-6">
            <span className="text-sm text-neutral-500 dark:text-neutral-400">Uploading…</span>
            {progress !== undefined && <ProgressBar progress={progress} progressId={progressId} />}
          </div>
        ) : variant === 'camera' ? (
          <>
            <span className="text-neutral-400 dark:text-neutral-500"><CameraIcon /></span>
            <span className="text-sm text-neutral-500 dark:text-neutral-400">Tap to take a photo</span>
          </>
        ) : (
          <>
            <span className={dragging ? 'text-brand-500' : 'text-neutral-400 dark:text-neutral-500'}><UploadIcon /></span>
            <div className="text-center">
              <span className="text-sm font-medium text-brand-600 dark:text-brand-400">Click to upload</span>
              <span className="text-sm text-neutral-500 dark:text-neutral-400"> or drag and drop</span>
            </div>
            <span className="text-xs text-neutral-400 dark:text-neutral-500">
              {accept ? describeAccept(accept) : 'Any file'} · max {formatBytes(maxSize)}
            </span>
          </>
        )}
      </label>

      {fileInput}

      {hasError && <ErrorText id={errorId} message={displayError!} />}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ProgressBar({ progress, progressId }: { progress: number; progressId: string }) {
  return (
    <div
      id={progressId}
      role="progressbar"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Upload progress"
      className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-1.5 overflow-hidden"
    >
      <div
        className="h-full bg-brand-500 transition-all duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

function ErrorText({ id, message }: { id: string; message: string }) {
  return (
    <span id={id} role="alert" className="text-xs text-danger-600 dark:text-danger-400">
      {message}
    </span>
  );
}
